import { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { useTheme } from 'next-themes';
import { getThemeById, ThemePreset } from '@/data/themePresets';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { syncStorage } from '@/lib/storage';

const PALETTE_STORAGE_KEY = 'orbit-theme-palette';

// Get initial palette from localStorage synchronously to prevent flash
const getInitialPalette = (): string => {
  if (typeof window === 'undefined') return 'default';
  return syncStorage.get(PALETTE_STORAGE_KEY) || 'default';
};

export const useThemePalette = () => {
  const { theme: mode, setTheme: setMode, resolvedTheme } = useTheme();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [mounted, setMounted] = useState(false);
  const [localPalette, setLocalPalette] = useState(getInitialPalette);

  // Fetch palette from database if logged in
  const { data: profilePalette } = useQuery({
    queryKey: ['profile-palette', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('theme_palette')
        .eq('id', user.id)
        .single();
      if (error) throw error;
      return data?.theme_palette || 'default';
    },
    enabled: !!user,
  });

  // Mutation to save palette to database
  const savePaletteMutation = useMutation({
    mutationFn: async (paletteId: string) => {
      if (!user) return;
      const { error } = await supabase
        .from('profiles')
        .update({ theme_palette: paletteId })
        .eq('id', user.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile-palette', user?.id] });
    },
  });

  // Get palette - prioritize database when available, then localStorage
  const palette = profilePalette || localPalette;

  // Apply palette immediately on first render (before paint) to prevent flash
  useLayoutEffect(() => {
    const storedPalette = getInitialPalette();
    const isDark = document.documentElement.classList.contains('dark') || 
                   window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    // Apply immediately before first paint
    const themePreset = getThemeById(storedPalette);
    if (themePreset && storedPalette !== 'default') {
      const themeVars = isDark ? themePreset.dark : themePreset.light;
      Object.entries(themeVars).forEach(([key, value]) => {
        document.documentElement.style.setProperty(key, value);
      });
      document.documentElement.setAttribute('data-palette', storedPalette);
    }
  }, []);

  // Apply palette styles on mount and when palette/theme changes
  useEffect(() => {
    setMounted(true);
  }, []);

  const applyPaletteStyles = useCallback((paletteId: string, isDark: boolean) => {
    const themePreset = getThemeById(paletteId);
    const root = document.documentElement;

    // Remove any existing theme data attribute
    root.removeAttribute('data-palette');

    if (!themePreset || paletteId === 'default') {
      // Reset to default by removing every property we might have set
      // on a non-default palette. Without sidebar tokens in this list,
      // switching back from Andromeda/Earth would leave the rail stuck
      // on the previous palette's colors.
      const customProps = [
        '--background', '--foreground', '--card', '--card-foreground',
        '--primary', '--primary-foreground', '--secondary', '--secondary-foreground',
        '--muted', '--muted-foreground', '--accent', '--accent-foreground',
        '--border', '--ring',
        '--sidebar-background', '--sidebar-foreground',
        '--sidebar-primary', '--sidebar-primary-foreground',
        '--sidebar-accent', '--sidebar-accent-foreground',
        '--sidebar-border', '--sidebar-ring',
      ];
      customProps.forEach(prop => {
        root.style.removeProperty(prop);
      });
      return;
    }

    // Apply the theme's custom properties
    const themeVars = isDark ? themePreset.dark : themePreset.light;
    Object.entries(themeVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    root.setAttribute('data-palette', paletteId);
  }, []);

  // Apply palette when it changes or resolvedTheme changes
  useEffect(() => {
    if (mounted && palette) {
      applyPaletteStyles(palette, resolvedTheme === 'dark');
    }
  }, [resolvedTheme, mounted, palette, applyPaletteStyles]);

  const setPalette = useCallback((paletteId: string) => {
    // Update local state immediately
    setLocalPalette(paletteId);
    
    // Always save to storage as backup
    syncStorage.set(PALETTE_STORAGE_KEY, paletteId);
    
    // Save to database if logged in
    if (user) {
      savePaletteMutation.mutate(paletteId);
    }
    
    // Apply immediately
    applyPaletteStyles(paletteId, resolvedTheme === 'dark');
  }, [user, resolvedTheme, applyPaletteStyles, savePaletteMutation]);

  const currentTheme = getThemeById(palette);

  return {
    palette,
    setPalette,
    mode,
    setMode,
    resolvedTheme,
    currentTheme,
    mounted,
  };
};
